/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
/* eslint-disable @atlassian/perf-linting/no-chain-state-updates -- the coordinator deliberately
 * groups multiple state slices behind subscription callbacks; the lint rule's static
 * analysis can't tell that branches like `setSelectedTarget(null)` in different effect
 * paths aren't actually chained, only conditionally exclusive.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { bindAll } from 'bind-event-listener';

import type { ExtractInjectionAPI, UiComponentFactoryParams } from '@atlaskit/editor-common/types';
import { findDomRefAtPos } from '@atlaskit/editor-prosemirror/utils';
import { akEditorDefaultLayoutWidth } from '@atlaskit/editor-shared-styles';
import type { AlignmentValue } from '@atlaskit/native-embeds-common';

import type {
	EditorPluginNativeEmbedsPlugin,
	SelectedEmbedIdentity,
} from '../nativeEmbedsPluginType';
import { pluginKey } from '../pm-plugins/plugin-state';
import {
	getSelectedNativeEmbedExtension,
	isNativeEmbedNode,
} from '../pm-plugins/utils/getSelectedNativeEmbedExtension';

import { EmbedResizeOverlay, type Target } from './EmbedResizeOverlay';

// Test id is exported from utils for shared use; importers should use that path.
// (Intentionally not re-exported here to satisfy the @atlaskit/editor/no-re-export rule.)

// Selection-related state grouped together so that subscriptions to the
// shared state can update everything in a single setState call (one render).
// Hoisted to module scope so it isn't re-declared on every coordinator render.
type SelectionState = {
	currentAlignment: AlignmentValue;
	hoveredEmbedPos: number | null;
	selectedEmbed: SelectedEmbedIdentity | null;
	trackedEmbedPos: number | null;
};

export type ResizableNativeEmbedCoordinatorProps = UiComponentFactoryParams & {
	pluginInjectionApi: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined;
};

/**
 * Coordinator that decides which embeds (selected and/or hovered) currently
 * warrant a resize overlay. The actual per-embed overlay rendering and resize
 * lifecycle lives in `EmbedResizeOverlay`.
 *
 * Up to 2 overlays can be mounted at once: one for the currently-selected
 * embed and one for a separately-hovered embed (when they differ).
 *
 * NOTE: Hover detection relies on `mouseover`/`mouseout` bubbling from the
 * editor content area. Mouseover events from inside a cross-origin iframe do
 * not bubble to the outer document, so the hover overlay simply will not
 * appear while the cursor is inside an embed iframe's content. This is an
 * accepted limitation.
 */
export const ResizableNativeEmbedCoordinator = (props: ResizableNativeEmbedCoordinatorProps) => {
	const { pluginInjectionApi, disabled, editorView: view, appearance } = props;

	const initialWidthState = pluginInjectionApi?.width.sharedState.currentState();
	// State for width plugin subscription. We intentionally store both lineLength
	// and width in a single object so that updates from the width subscription
	// are applied atomically (avoids the no-chain-state-updates rule).
	const [widthState, setWidthState] = useState(() => ({
		lineLength: initialWidthState?.lineLength || akEditorDefaultLayoutWidth,
		containerWidth: initialWidthState?.width || akEditorDefaultLayoutWidth,
	}));
	const { lineLength, containerWidth } = widthState;

	useEffect(() => {
		const unsubscribe = pluginInjectionApi?.width.sharedState.onChange(({ nextSharedState }) => {
			if (nextSharedState) {
				setWidthState({
					lineLength: nextSharedState.lineLength || akEditorDefaultLayoutWidth,
					containerWidth: nextSharedState.width || akEditorDefaultLayoutWidth,
				});
			}
		});
		return () => {
			unsubscribe?.();
		};
	}, [pluginInjectionApi]);

	const [selectionState, setSelectionState] = useState<SelectionState>(() => {
		const initial = pluginInjectionApi?.editorPluginNativeEmbeds?.sharedState?.currentState();
		return {
			selectedEmbed: initial?.selectedEmbed ?? null,
			trackedEmbedPos: null,
			hoveredEmbedPos: null,
			currentAlignment: initial?.selectedEmbed?.alignment ?? 'center',
		};
	});
	const { selectedEmbed, trackedEmbedPos, hoveredEmbedPos, currentAlignment } = selectionState;

	// Coordinator-wide single source of truth for "which embed is currently
	// being resized" (by localId). At most one overlay can own a resize at a
	// time; second-start attempts are rejected. Stored in a ref so hover and
	// shared-state callbacks can read it synchronously without re-render.
	const resizingLocalIdRef = useRef<string | null>(null);

	// Tracks the last `pos` we dispatched in a SET_HOVERED_EMBED_POS meta.
	// We dispatch when the *position* changes (not only when the localId
	// changes) so that re-hovering the same embed after a doc transaction
	// re-syncs the plugin to the embed's latest pos rather than leaving it
	// pinned to a stale value. `null` means "no hover position currently
	// tracked by the plugin".
	const lastDispatchedHoverPosRef = useRef<number | null>(null);

	// Mirror of `resizingLocalIdRef` as state, so render can keep the
	// resize-owning overlay mounted across re-renders even if shared-state
	// updates would otherwise dedup it (e.g. selection transferring to the
	// same embed mid-drag).
	const [resizingLocalId, setResizingLocalId] = useState<string | null>(null);

	// Resolve the selected target (DOM + node) ------------------------------
	const [selectedTarget, setSelectedTarget] = useState<Target | null>(null);
	const [hoveredTarget, setHoveredTarget] = useState<Target | null>(null);

	const handleAlignmentChange = useCallback((next: AlignmentValue) => {
		setSelectionState((prev) => ({ ...prev, currentAlignment: next }));
	}, []);

	/**
	 * Called by a child overlay when it wants to start a resize. Returns
	 * `true` if the start is accepted (this overlay now owns the resize), or
	 * `false` if another overlay is already mid-resize and the start should
	 * be rejected.
	 *
	 * NOTE: We deliberately do NOT clear `hoveredTarget` here, even though
	 * Tier 1 (e) of the hardening plan suggested it. Clearing it synchronously
	 * unmounts the hover overlay that just started the drag (because the
	 * render tree no longer includes it), which destroys the ResizerNext
	 * subtree mid-mousedown and the drag never reaches `handleResizeStart`.
	 * The render-time dedup (`hoveredTarget.localId !== selectedTarget?.localId`)
	 * is sufficient to prevent two overlays for the same embed.
	 */
	const handleResizeStart = useCallback((localId: string): boolean => {
		if (resizingLocalIdRef.current !== null && resizingLocalIdRef.current !== localId) {
			// Another overlay is already mid-resize; reject the second start.
			return false;
		}
		resizingLocalIdRef.current = localId;
		setResizingLocalId(localId);
		return true;
	}, []);

	/**
	 * Called by a child overlay when its resize sequence has finished. We
	 * ignore stops for any localId other than the one currently owning the
	 * resize — defends against the unmount-cleanup race where a hover overlay
	 * is unmounted (because its embed becomes selected) while the *selected*
	 * overlay is still mid-resize.
	 */
	const handleResizeStop = useCallback((localId: string) => {
		if (resizingLocalIdRef.current !== localId) {
			return;
		}
		resizingLocalIdRef.current = null;
		setResizingLocalId(null);
		// Tier 1 (e): unconditionally clear hovered after a resize completes;
		// it will re-acquire on the next pointer movement if the cursor is
		// still inside an embed. (Safe here because the drag has already
		// finished; we're not unmounting an active ResizerNext subtree.)
		setHoveredTarget(null);
	}, []);

	useEffect(() => {
		// Tier 1 (c): we deliberately do NOT short-circuit when a resize is in
		// progress. The PM `is-resizer-resizing` meta + plugin-state already
		// prevents the selection from being clobbered by the resize itself,
		// so dropping selection updates client-side just creates drift between
		// the coordinator's view of "selected" and the doc's truth.
		const unsub = pluginInjectionApi?.editorPluginNativeEmbeds?.sharedState?.onChange?.(
			({ nextSharedState }) => {
				const nextEmbed = nextSharedState?.selectedEmbed ?? null;

				setSelectionState((prev) => ({
					selectedEmbed: nextEmbed,
					trackedEmbedPos: nextSharedState?.trackedEmbedPos ?? null,
					hoveredEmbedPos: nextSharedState?.hoveredEmbedPos ?? null,
					currentAlignment: nextEmbed !== null ? nextEmbed.alignment : prev.currentAlignment,
				}));
			},
		);
		return () => {
			unsub?.();
		};
	}, [pluginInjectionApi]);

	useEffect(() => {
		if (!view) {
			setSelectedTarget(null);
			return;
		}

		const selected = getSelectedNativeEmbedExtension(view.state);
		if (!selected) {
			setSelectedTarget(null);
			return;
		}

		try {
			const posForLookup = trackedEmbedPos ?? selected.pos;
			const domNode = findDomRefAtPos(posForLookup, view.domAtPos.bind(view));
			const element =
				domNode instanceof HTMLElement ? domNode : (domNode.parentElement as HTMLElement | null);
			if (!element) {
				setSelectedTarget(null);
				return;
			}
			const container =
				(element.querySelector('.extension-container') as HTMLElement | null) ??
				(element.closest('.extension-container') as HTMLElement | null) ??
				element;
			const innerContainer =
				container.querySelector<HTMLElement>('[data-testid="native-embed-extension"]') ?? null;

			const localId: string = selected.node.attrs?.localId ?? '';

			setSelectedTarget({
				pos: selected.pos,
				localId,
				node: selected.node,
				container,
				innerContainer,
			});
		} catch {
			setSelectedTarget(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedEmbed, view, view?.state, trackedEmbedPos]);

	// Resolve the portal target (the editor's scroll container)
	const portalTarget = useMemo<HTMLElement | null>(() => {
		if (!view) {
			return null;
		}
		return (
			(view.dom.closest('.fabric-editor-popup-scroll-parent') as HTMLElement | null) ||
			(view.dom.closest('.ak-editor-content-area') as HTMLElement | null) ||
			view.dom.parentElement ||
			null
		);
	}, [view]);

	/**
	 * Given any element inside a native-embed nodeview, walks up to the
	 * `extensionView-content-wrap` element which is the actual ProseMirror
	 * nodeView wrapper. `view.posAtDOM(wrapper, 0)` then resolves to the
	 * exact position of THIS embed in the document — which is the only
	 * reliable way to disambiguate sibling embeds. Calling posAtDOM on
	 * the inner `.extension-container` returns positions inside the embed
	 * (or, for sibling embeds, can collapse to a neighbouring embed's
	 * position depending on layout), which is what caused the bug where
	 * resizing embed B resized embed C.
	 *
	 * Lifted to component scope so both the `mouseover` hover detector and
	 * the doc-change re-resolution effect can share the same logic.
	 */
	const resolveTargetFromAnyElement = useCallback(
		(anyEl: HTMLElement): Target | null => {
			if (!view) {
				return null;
			}
			try {
				let nodeViewDom: HTMLElement | null = anyEl;
				while (
					nodeViewDom &&
					nodeViewDom !== view.dom &&
					!nodeViewDom.classList.contains('extensionView-content-wrap')
				) {
					nodeViewDom = nodeViewDom.parentElement;
				}
				if (!nodeViewDom || nodeViewDom === view.dom) {
					return null;
				}

				const pos = view.posAtDOM(nodeViewDom, 0);
				const $pos = view.state.doc.resolve(pos);

				let node: ReturnType<typeof view.state.doc.nodeAt> | null = null;
				let nodePos: number | null = null;

				const nodeAfter = $pos.nodeAfter;
				if (nodeAfter && isNativeEmbedNode(nodeAfter)) {
					node = nodeAfter;
					nodePos = pos;
				}

				if (!node) {
					for (let d = $pos.depth; d >= 0; d--) {
						const candidate = $pos.node(d);
						if (candidate && isNativeEmbedNode(candidate)) {
							node = candidate;
							nodePos = d === 0 ? 0 : $pos.before(d);
							break;
						}
					}
				}

				if (!node || nodePos == null) {
					return null;
				}

				const container =
					nodeViewDom.querySelector<HTMLElement>('.extension-container') ?? nodeViewDom;
				const innerContainer =
					container.querySelector<HTMLElement>('[data-testid="native-embed-extension"]') ?? null;

				return {
					pos: nodePos,
					localId: node.attrs?.localId ?? '',
					node,
					container,
					innerContainer,
				};
			} catch {
				return null;
			}
		},
		[view],
	);

	// Hover detector --------------------------------------------------------
	useEffect(() => {
		if (!view) {
			return;
		}
		// Listen on view.dom itself (the contenteditable). This is the most
		// reliable place to capture mouseover/mouseout on embeds: any other
		// container we'd pick (.ak-editor-content-area, .fabric-editor-
		// popup-scroll-parent, etc) may not be present in every editor surface.
		// Embed iframes don't bubble mouse events to the parent document, but
		// the wrapping `.extension-container` element does — that's what we're
		// tracking here.
		if (!(view.dom instanceof HTMLElement)) {
			return;
		}
		const root: HTMLElement = view.dom;

		const isInsideEmbedOrOverlay = (n: Node | null): boolean => {
			if (!n) {
				return false;
			}
			if (!(n instanceof Element)) {
				return n.parentElement ? isInsideEmbedOrOverlay(n.parentElement) : false;
			}
			return (
				!!n.closest('.extension-container') || !!n.closest('[data-testid="native-embed-resizer"]')
			);
		};

		// Tier 1 (d): debounce hover transitions. Rapid mouseover/mouseout
		// pairs (e.g. crossing internal element boundaries inside an embed
		// wrapper) would otherwise flicker `hoveredTarget` and produce brief
		// two-overlay frames. A short debounce coalesces the burst into a
		// single transition.
		const HOVER_DEBOUNCE_MS = 75;
		let pendingHoverTimeout: ReturnType<typeof setTimeout> | null = null;
		const cancelPendingHover = () => {
			if (pendingHoverTimeout !== null) {
				clearTimeout(pendingHoverTimeout);
				pendingHoverTimeout = null;
			}
		};

		const handleOver = (event: Event) => {
			if (resizingLocalIdRef.current !== null) {
				return;
			}
			const evtTarget = event.target;
			if (!(evtTarget instanceof HTMLElement)) {
				return;
			}
			// Only trigger when the mouse is over an actual native-embed
			// (avoids firing for arbitrary editor content). The element we
			// look up is the inner iframe wrapper; we then resolve up to the
			// nodeView wrapper to disambiguate sibling embeds.
			const inner = evtTarget.closest<HTMLElement>('[data-testid="native-embed-extension"]');
			if (!inner) {
				return;
			}

			const newTarget = resolveTargetFromAnyElement(inner);
			if (!newTarget) {
				return;
			}

			cancelPendingHover();
			pendingHoverTimeout = setTimeout(() => {
				pendingHoverTimeout = null;
				if (resizingLocalIdRef.current !== null) {
					return;
				}
				// Dispatch the PM-world bridge OUTSIDE the setState updater. React
				// may invoke updaters more than once (StrictMode, concurrent
				// rendering); a side-effect inside would then dispatch duplicate
				// transactions. We compare against `lastDispatchedHoverPosRef` —
				// not just localId — so re-hovering the same embed after a doc
				// shift re-syncs the plugin to the embed's latest pos.
				//
				// Collab safety: this transaction has no steps and `tr.docChanged`
				// is false, so the collab plugin's `send-transaction.ts` filter
				// (which requires docChanged) will not broadcast it. Verified
				// against editor-plugin-collab-edit/.../send-transaction.ts.
				if (view && lastDispatchedHoverPosRef.current !== newTarget.pos) {
					lastDispatchedHoverPosRef.current = newTarget.pos;
					const tr = view.state.tr
						.setMeta(pluginKey, {
							type: 'SET_HOVERED_EMBED_POS',
							pos: newTarget.pos,
						})
						// Hover dispatches must never enter the undo stack — they
						// are pure UI state, not a user-visible doc edit.
						.setMeta('addToHistory', false);
					view.dispatch(tr);
				}
				setHoveredTarget((current) =>
					current?.localId === newTarget.localId ? current : newTarget,
				);
			}, HOVER_DEBOUNCE_MS);
		};

		const handleOut = (event: Event) => {
			if (resizingLocalIdRef.current !== null) {
				return;
			}
			const me = event as MouseEvent;
			const related = me.relatedTarget;
			if (related instanceof Node && isInsideEmbedOrOverlay(related)) {
				return;
			}

			cancelPendingHover();
			pendingHoverTimeout = setTimeout(() => {
				pendingHoverTimeout = null;
				if (resizingLocalIdRef.current !== null) {
					return;
				}
				// Clear the tracked hover position so the plugin stops mapping
				// it through transactions. Dispatch outside the setState updater
				// (see handleOver above for rationale) and only when the plugin
				// currently has a non-null pos to clear.
				if (view && lastDispatchedHoverPosRef.current !== null) {
					lastDispatchedHoverPosRef.current = null;
					const tr = view.state.tr
						.setMeta(pluginKey, {
							type: 'SET_HOVERED_EMBED_POS',
							pos: null,
						})
						.setMeta('addToHistory', false);
					view.dispatch(tr);
				}
				setHoveredTarget(null);
			}, HOVER_DEBOUNCE_MS);
		};

		const unbind = bindAll(root, [
			{ type: 'mouseover', listener: handleOver },
			{ type: 'mouseout', listener: handleOut },
		]);
		return () => {
			cancelPendingHover();
			unbind();
		};
	}, [view, resolveTargetFromAnyElement]);

	// Hover-overlay alignment with doc changes (CNS-26923 parity for hover).
	// When `hoveredEmbedPos` changes (the PM plugin mapped it through a doc
	// transaction) we re-resolve the hover Target so EmbedResizeOverlay's
	// `useLayoutEffect([target])` re-measures against the embed's current
	// rect. See commit message for the full data-flow rationale.
	//
	// `hoveredTarget` is intentionally omitted from deps: it is owned by
	// this same coordinator and only the `hoveredEmbedPos` change should
	// trigger re-resolution. The `resolveTargetFromAnyElement` dep is for
	// react-hooks/exhaustive-deps compliance only — it changes iff `view`
	// changes (already in the dep list).
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (!view || !hoveredTarget || hoveredEmbedPos === null) {
			return;
		}
		// Don't disturb an in-flight resize: rebuilding Target identity
		// re-fires EmbedResizeOverlay's measurement effect mid-drag.
		if (resizingLocalIdRef.current !== null) {
			return;
		}

		try {
			const domNode = findDomRefAtPos(hoveredEmbedPos, view.domAtPos.bind(view));
			const element =
				domNode instanceof HTMLElement ? domNode : (domNode.parentElement as HTMLElement | null);
			if (!element) {
				return;
			}
			const refreshed = resolveTargetFromAnyElement(element);
			if (!refreshed || refreshed.localId !== hoveredTarget.localId) {
				// The mapped pos resolves to a different embed or nothing —
				// leave the hover target alone; the defensive cleanup handles
				// the case where the hovered embed was deleted.
				return;
			}
			// Hand EmbedResizeOverlay a fresh Target object so its
			// `useLayoutEffect([target, ...])` re-fires and re-measures
			// the embed's current getBoundingClientRect().
			setHoveredTarget(refreshed);
		} catch {
			// DOM lookup can throw mid-transaction; leave the stale target in
			// place and let the next hoveredEmbedPos change retry.
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hoveredEmbedPos, view, resolveTargetFromAnyElement]);

	// Defensive: clear hovered target if its DOM is gone (e.g. collab edit
	// removed the embed) or if it's now also the selected target. We use the
	// view's owner document for the check rather than the global `document`
	// to avoid cross-iframe surprises.
	//
	// IMPORTANT: never clear the hovered target if it currently owns an
	// in-flight resize (e.g. selection just transferred to the embed the
	// hover overlay is dragging). Doing so would unmount the ResizerNext
	// subtree mid-drag and silently cancel the resize.
	useEffect(() => {
		if (!hoveredTarget) {
			return;
		}
		if (resizingLocalIdRef.current === hoveredTarget.localId) {
			return;
		}
		const ownerDoc = view?.dom.ownerDocument;
		const inDom = ownerDoc ? ownerDoc.contains(hoveredTarget.container) : false;
		const sameAsSelected = !!selectedTarget && hoveredTarget.localId === selectedTarget.localId;
		if (!inDom || sameAsSelected) {
			setHoveredTarget(null);
		}
	}, [hoveredTarget, view, selectedTarget]);

	if (!view || !portalTarget) {
		return null;
	}

	// Render gating ---------------------------------------------------------
	// We may have a `selectedTarget` and `hoveredTarget` that resolve to the
	// same embed (e.g. mid-drag the selection transfers to the embed the
	// hover overlay is currently resizing). In that case we must NOT unmount
	// the hover overlay — its ResizerNext subtree is mid-drag and unmounting
	// it would cancel the resize. Instead, we suppress the *other* overlay
	// for that frame so we still render exactly one overlay per embed.
	const hoverEqualsSelected =
		!!hoveredTarget && !!selectedTarget && hoveredTarget.localId === selectedTarget.localId;
	const hoverIsResizing = !!hoveredTarget && resizingLocalId === hoveredTarget.localId;
	const selectedIsResizing = !!selectedTarget && resizingLocalId === selectedTarget.localId;

	// When the same embed is both selected and hovered:
	// - If the hover overlay owns the in-flight resize, keep hover, drop
	//   selected (the selected overlay would otherwise dedup hover and
	//   cancel the drag).
	// - Otherwise prefer the selected overlay (it has the alignment toolbar
	//   and the existing dedup behaviour).
	const renderSelected =
		!!selectedTarget && !(hoverEqualsSelected && hoverIsResizing && !selectedIsResizing);
	const renderHover =
		!!hoveredTarget && (!hoverEqualsSelected || (hoverIsResizing && !selectedIsResizing));

	return (
		<>
			{renderSelected && selectedTarget && (
				<EmbedResizeOverlay
					key={`sel-${selectedTarget.localId}`}
					target={selectedTarget}
					mode="selected"
					view={view}
					portalTarget={portalTarget}
					pluginInjectionApi={pluginInjectionApi}
					appearance={appearance}
					disabled={!!disabled}
					containerWidth={containerWidth}
					lineLength={lineLength}
					currentAlignment={currentAlignment}
					onAlignmentChange={handleAlignmentChange}
					onResizeStart={handleResizeStart}
					onResizeStop={handleResizeStop}
				/>
			)}
			{renderHover && hoveredTarget && (
				<EmbedResizeOverlay
					key={`hov-${hoveredTarget.localId}`}
					target={hoveredTarget}
					mode="hover"
					view={view}
					portalTarget={portalTarget}
					pluginInjectionApi={pluginInjectionApi}
					appearance={appearance}
					disabled={!!disabled}
					containerWidth={containerWidth}
					lineLength={lineLength}
					currentAlignment="center"
					onResizeStart={handleResizeStart}
					onResizeStop={handleResizeStop}
				/>
			)}
		</>
	);
};
